import { useCallback, useState } from 'react'
import { Button, Chip, Spinner } from '@heroui/react'

import { getRetrievalRecordFor } from '../services/retrievalRecordCache'
import type { RetrievalReviewItem } from '../services'
import { curationDeepLink } from '../utils/curationDeepLink'

interface RecordPeekProps {
  gifId: string
}

/** Fetch states for the lazily-loaded retrieval record. */
type PeekState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; record: RetrievalReviewItem | null }
  | { status: 'error'; message: string }

/** Small labeled list block (scenario examples / exclusion conditions). */
function RecordList({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null
  return (
    <div>
      <p className="text-[11px] font-medium text-default-600">{label}</p>
      <ul className="mt-0.5 list-disc pl-4 space-y-0.5 text-xs text-default-500">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Collapsed-by-default disclosure that lazily surfaces a gif's retrieval record
 * (the AI-written narrative the whole recommendation pipeline runs on) so the
 * curator can tell a genuine trap from a mis-described record without leaving
 * the deck.
 *
 * Collapsed by default is deliberate: the curator should react to the GIF
 * first, then check the record — the peek is a tiebreaker, not the headline.
 * The record is fetched only on first expansion (via the shared single-flight
 * cache), never on page load.
 */
export function RecordPeek({ gifId }: RecordPeekProps) {
  const [expanded, setExpanded] = useState(false)
  const [state, setState] = useState<PeekState>({ status: 'idle' })

  const fetchRecord = useCallback(async () => {
    setState({ status: 'loading' })
    try {
      const record = await getRetrievalRecordFor(gifId)
      setState({ status: 'loaded', record })
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : String(err),
      })
    }
  }, [gifId])

  const toggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev
      // Fetch on the first expansion, or retry after a prior failure.
      if (next && (state.status === 'idle' || state.status === 'error')) {
        void fetchRecord()
      }
      return next
    })
  }, [state.status, fetchRecord])

  return (
    <div className="w-full max-w-md rounded-lg border border-default-200 dark:border-default-100/20">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs text-default-500 hover:text-default-600"
        aria-expanded={expanded}
      >
        <span className="font-medium">What the record says</span>
        <span aria-hidden className="text-default-400">
          {expanded ? '−' : '+'}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-default-200 dark:border-default-100/20 px-3 py-2 space-y-2">
          {state.status === 'loading' && (
            <div className="flex justify-center py-3">
              <Spinner size="sm" />
            </div>
          )}

          {state.status === 'error' && (
            <p className="text-xs text-red-600 dark:text-red-400">
              Couldn&apos;t load the record: {state.message}
            </p>
          )}

          {state.status === 'loaded' && state.record === null && (
            <p className="text-xs text-default-500">
              No retrieval record for this gif.
            </p>
          )}

          {state.status === 'loaded' && state.record && (
            <>
              <p className="text-xs text-default-600">
                {state.record.narrative}
              </p>

              <RecordList
                label="Use when"
                items={state.record.scenarioExamples ?? []}
              />
              <RecordList
                label="Don't use when"
                items={state.record.exclusionConditions ?? []}
              />
              <RecordList
                label="Distinctive"
                items={state.record.distinctiveFeatures ?? []}
              />

              <div className="flex flex-wrap gap-1">
                <Chip size="sm" variant="flat" color="default">
                  tone: {state.record.toneAffordances}
                </Chip>
                <Chip size="sm" variant="flat" color="default">
                  perspective: {state.record.perspectiveAffordances}
                </Chip>
              </div>
            </>
          )}

          {/* The record fix link lives here so the curator sees the record
              first, then decides it's off. */}
          <Button
            as="a"
            href={curationDeepLink(gifId)}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            variant="light"
            className="text-default-400 h-auto min-h-0 py-0.5"
          >
            Record looks off → fix in Curation
          </Button>
        </div>
      )}
    </div>
  )
}
